import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import LoadingSpinner from "../../../common/LoadingSpinner";
import { SonicFacilityData } from "../../../common/classes/SonicFacility";
import Status from "../../../common/management/detail/Status";
import ActionBtn from "../../../common/management/ActionBtn";
import Modal from "../../../common/management/Modal";
import NotificationModal from "../../../common/settings-modal/NotificationModal";
import {
  RequestState,
  genNotificationColor,
  genNotificationMessage,
} from "../../../utils/RequestState";
import { CategoryState } from "../../../utils/CategoryState";
import styles from "../db-list/detail/DatabaseDetail.module.scss";
import SelenaA from "../../../assets/img/service/selena-a.webp";
import SonicLink from "../../../common/routing/SonicLink";

type Props = {
  children: JSX.Element;
  facility: SonicFacilityData;
  isLoading: boolean;
  requestState: RequestState;
  setRequestState: (value: RequestState) => void;
  error: boolean;
  selectStatus?: string;
  setSelectStatus?: (value: string, name: string) => void;
  showStatusMenu?: boolean;
  handleStatusMenu?: () => void;
  handleUpdate: () => void;
  handleDelete: () => void;
};

const FacilityDetail: React.FC<Props> = ({
  children,
  facility,
  isLoading,
  requestState,
  setRequestState,
  error,
  selectStatus,
  setSelectStatus,
  showStatusMenu,
  handleStatusMenu,
  handleUpdate,
  handleDelete,
}) => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [processState, setProcessState] = useState<"update" | "delete">(
    "update"
  );

  return (
    <div className={styles["f-databaseDetail"]}>
      {isLoading || !facility ? (
        <LoadingSpinner space={1} admin_page={true} />
      ) : (
        <>
          <h1 className={styles["f-databaseDetail__title"]}>施設詳細</h1>
          <div className={styles["f-databaseDetail__inner"]}>
            <div className={styles["f-databaseDetail__head"]}>
              <Status
                isVisibilityStatus={true}
                status={facility.status}
                selectStatus={selectStatus}
                showStatusMenu={showStatusMenu}
                handleStatusMenu={handleStatusMenu}
                setSelectStatus={setSelectStatus}
              />
              {facility.owner && (
                <div className={styles["f-databaseDetail__user"]}>
                  <div className={styles["f-databaseDetail__user-head"]}>
                    <h2
                      className={styles["f-databaseDetail__user-head__title"]}
                    >
                      ユーザー情報
                    </h2>
                    <SonicLink to={`/admin/member/${facility.owner.id}`}>
                      ユーザー詳細
                    </SonicLink>
                  </div>
                  <div className={styles["f-databaseDetail__user-info"]}>
                    <div>
                      <p>ユーザー名</p>
                      <span>{facility.owner.name}</span>
                    </div>
                    <div>
                      <p>メールアドレス</p>
                      <span>{facility.owner.email}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {children}
            {error ? (
              <div className={styles["f-databaseDetail__error"]}>
                必須項目が未入力、または入力内容に誤りがあります
              </div>
            ) : null}
            <div className={styles["f-databaseDetail__bottom"]}>
              <ActionBtn
                label=" 保存 "
                onClick={() => {
                  setProcessState("update");
                  handleUpdate();
                }}
                colorReversal={true}
              />
            </div>
            <div className={styles["f-databaseDetail__btn"]}>
              <div className={styles["f-databaseDetail__btn-box"]}>
                <ActionBtn
                  label=" 保存 "
                  onClick={() => {
                    setProcessState("update");
                    handleUpdate();
                  }}
                  colorReversal={true}
                />
                <button
                  onClick={() => {
                    setModalOpen(true);
                    setProcessState("delete");
                  }}
                  className={styles["f-databaseDetail__btn-box__delete"]}
                  type="button"
                >
                  <FontAwesomeIcon icon={["fal", "trash-alt"]} />
                  <span>削除</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {modalOpen && processState === "delete" && (
        <div className={styles["f-databaseDetail__modal-overlay"]}>
          <Modal
            closeModal={() => {
              setModalOpen(false);
              setRequestState(RequestState.Waiting);
              if (requestState === RequestState.Success)
                navigate("/admin/facility");
            }}
            handler={() => handleDelete()}
            categoryState={CategoryState.Delete}
            requestState={requestState}
            headText="施設削除"
            buttonText={
              requestState !== RequestState.Success &&
              requestState !== RequestState.Failed ? (
                <>
                  <FontAwesomeIcon icon={["fal", "trash-alt"]} />
                  <span>削除する</span>
                </>
              ) : undefined
            }
          >
            {requestState === RequestState.Success ? (
              <>施設を削除しました</>
            ) : requestState === RequestState.Failed ? (
              <>施設削除に失敗しました。</>
            ) : requestState === RequestState.Doing ? (
              <LoadingSpinner space={1} />
            ) : (
              <>
                施設を削除すると復元できません。
                <br />
                下記施設を削除してよろしいですか？
                <p className={styles["f-databaseDetail__modal-name"]}>
                  {facility.name}
                </p>
              </>
            )}
          </Modal>
        </div>
      )}
      {requestState !== RequestState.Waiting && processState === "update" ? (
        <NotificationModal
          color={genNotificationColor(requestState)}
          message={genNotificationMessage(requestState)}
          img_src={requestState === RequestState.Success ? SelenaA : undefined}
        />
      ) : null}
    </div>
  );
};

export default FacilityDetail;
